import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import SouthEastRoundedIcon from '@mui/icons-material/SouthEastRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getPatternTitle, managedPatternOptions } from './patternTutorialData';
import { fetchScannerOverview, recordPatternScannerPageView } from './scannerExperienceApi';
import {
  AVAILABLE_PATTERN_ENGINE_VERSIONS,
  AVAILABLE_TRADE_ENGINE_VERSIONS,
  DEFAULT_PATTERN_ENGINE_VERSION,
  DEFAULT_SCANNER_EXCHANGE,
  DEFAULT_SCANNER_TIMEFRAME,
  DEFAULT_TRADE_ENGINE_VERSION,
  DEFAULT_SCANNER_UNIVERSE,
  NO_CLEAR_PATTERN_KEY,
  SCANNER_PAGE_SIZE,
  formatRelativeTargetMove,
  hasAdvancedSignalCoverage,
  resolveDisplayedPatternKey,
  resolveGeometryTypeLabel,
  resolvePatternEngineV2Metadata,
  resolveAverageScore,
  resolveAlignmentDescriptor,
  resolveDirectionDescriptor,
  resolveRiskDescriptor,
  resolveScore,
  resolveVolumeDescriptor,
} from './scannerExperienceShared';

const DEFAULT_FILTERS = {
  universe: DEFAULT_SCANNER_UNIVERSE,
  exchange: DEFAULT_SCANNER_EXCHANGE,
  timeframe: DEFAULT_SCANNER_TIMEFRAME,
  patternEngineVersion: DEFAULT_PATTERN_ENGINE_VERSION,
  tradeEngineVersion: DEFAULT_TRADE_ENGINE_VERSION,
  assetQuery: '',
  pattern: '',
  direction: 'ALL',
  convictionMin: 65,
  averageScoreMin: 65,
  confirmedVolumeOnly: false,
  volumeFilter: 'ALL',
  advancedOnly: false,
  topMoversOnly: false,
  page: 1,
};

const MIN_SCORE_THRESHOLD = 0;
const MAX_SCORE_THRESHOLD = 100;

const SCANNER_FILTERS_STORAGE_KEY = 'ecotoneai_scannerOverviewFilters';
const ANONYMOUS_SCANNER_RESULT_LIMIT = 3;
const SCANNER_FILTER_QUERY_KEYS = [
  'universe',
  'exchange',
  'timeframe',
  'patternEngineVersion',
  'tradeEngineVersion',
  'asset',
  'symbol',
  'search',
  'query',
  'pattern',
  'direction',
  'scoreMin',
  'convictionMin',
  'averageScoreMin',
  'confirmedVolumeOnly',
  'volumeFilter',
  'advancedOnly',
  'topMoversOnly',
  'page',
];

const BULLISH_DISPLAY_PATTERNS = new Set([
  'ASCENDING_TRIANGLE',
  'BULL_FLAG',
  'CUP_AND_HANDLE',
  'FALLING_WEDGE',
  'DOUBLE_BOTTOM',
  'INVERSE_HEAD_AND_SHOULDERS',
  'TRIPLE_BOTTOM',
  'ROUNDED_BOTTOM',
  'ACCUMULATION_BASE',
  'CHANNEL_BREAKOUT',
  'BREAKOUT_RANGE',
]);

const BEARISH_DISPLAY_PATTERNS = new Set([
  'DESCENDING_TRIANGLE',
  'BEAR_FLAG',
  'RISING_WEDGE',
  'DOUBLE_TOP',
  'HEAD_AND_SHOULDERS',
  'BREAKDOWN_RANGE',
  'DISTRIBUTION_TOP',
  'TRIPLE_TOP',
  'ROUNDED_TOP',
]);

function normalizeDirectionCode(value) {
  const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return normalized === 'BULLISH' || normalized === 'BEARISH' || normalized === 'NEUTRAL'
    ? normalized
    : 'NEUTRAL';
}

function normalizeScoreThreshold(value, fallback) {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }
  return Math.min(MAX_SCORE_THRESHOLD, Math.max(MIN_SCORE_THRESHOLD, Math.round(numericValue)));
}

function normalizeFilters(candidate = {}) {
  return {
    universe: typeof candidate.universe === 'string' && candidate.universe ? candidate.universe : DEFAULT_FILTERS.universe,
    exchange: typeof candidate.exchange === 'string' && candidate.exchange ? candidate.exchange : DEFAULT_FILTERS.exchange,
    timeframe: typeof candidate.timeframe === 'string' && candidate.timeframe ? candidate.timeframe : DEFAULT_FILTERS.timeframe,
    patternEngineVersion: Number.isFinite(Number(candidate.patternEngineVersion))
      ? Math.max(DEFAULT_PATTERN_ENGINE_VERSION, Number(candidate.patternEngineVersion))
      : DEFAULT_FILTERS.patternEngineVersion,
    tradeEngineVersion: Number.isFinite(Number(candidate.tradeEngineVersion))
      ? Math.max(DEFAULT_TRADE_ENGINE_VERSION, Number(candidate.tradeEngineVersion))
      : DEFAULT_FILTERS.tradeEngineVersion,
    assetQuery: typeof candidate.assetQuery === 'string' ? candidate.assetQuery : DEFAULT_FILTERS.assetQuery,
    pattern: typeof candidate.pattern === 'string' ? candidate.pattern : DEFAULT_FILTERS.pattern,
    direction: typeof candidate.direction === 'string' && candidate.direction ? candidate.direction : DEFAULT_FILTERS.direction,
    convictionMin: normalizeScoreThreshold(candidate.convictionMin, DEFAULT_FILTERS.convictionMin),
    averageScoreMin: normalizeScoreThreshold(candidate.averageScoreMin, DEFAULT_FILTERS.averageScoreMin),
    confirmedVolumeOnly: candidate.confirmedVolumeOnly === false ? false : candidate.confirmedVolumeOnly === true ? true : DEFAULT_FILTERS.confirmedVolumeOnly,
    volumeFilter: typeof candidate.volumeFilter === 'string' && candidate.volumeFilter ? candidate.volumeFilter : DEFAULT_FILTERS.volumeFilter,
    advancedOnly: candidate.advancedOnly === true,
    topMoversOnly: candidate.topMoversOnly === true,
    page: Math.max(1, Number(candidate.page) || DEFAULT_FILTERS.page),
  };
}

function normalizeStoredFilters(candidate = {}) {
  return {
    ...normalizeFilters(candidate),
    page: DEFAULT_FILTERS.page,
  };
}

function hasExplicitFilterParams(searchParams) {
  return SCANNER_FILTER_QUERY_KEYS.some((key) => searchParams.has(key));
}

function readStoredFilters() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return DEFAULT_FILTERS;
  }
  try {
    const rawValue = window.localStorage.getItem(SCANNER_FILTERS_STORAGE_KEY);
    if (!rawValue) {
      return DEFAULT_FILTERS;
    }
    return normalizeStoredFilters(JSON.parse(rawValue));
  } catch {
    return DEFAULT_FILTERS;
  }
}

function storeFilters(filters) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(SCANNER_FILTERS_STORAGE_KEY, JSON.stringify(normalizeStoredFilters(filters)));
  } catch {
    // Ignore persistence failures and keep the scanner usable.
  }
}

function shouldRetryScannerBootstrap(filters, payload) {
  const usesBroadDirectionFetch = filters.direction !== 'BULLISH' && filters.direction !== 'BEARISH';
  return Array.isArray(payload)
    && payload.length === 0
    && usesBroadDirectionFetch
    && !filters.topMoversOnly;
}

function buildActiveFilterChips(filters) {
  const chips = [];

  if (filters.universe !== DEFAULT_FILTERS.universe) {
    chips.push({ key: 'universe', label: `Univers : ${filters.universe.toUpperCase()}` });
  }
  if (filters.timeframe !== DEFAULT_FILTERS.timeframe) {
    chips.push({ key: 'timeframe', label: `Timeframe : ${filters.timeframe}` });
  }
  if (filters.patternEngineVersion !== DEFAULT_PATTERN_ENGINE_VERSION) {
    chips.push({ key: 'patternEngineVersion', label: `Pattern engine : v${filters.patternEngineVersion}` });
  }
  if (filters.tradeEngineVersion !== DEFAULT_TRADE_ENGINE_VERSION) {
    chips.push({ key: 'tradeEngineVersion', label: `Trade engine : v${filters.tradeEngineVersion}` });
  }
  if (filters.assetQuery) {
    chips.push({ key: 'assetQuery', label: `Actif : ${filters.assetQuery}` });
  }
  if (filters.direction !== DEFAULT_FILTERS.direction) {
    if (filters.direction === 'ALL') {
      chips.push({ key: 'direction', label: 'Direction : Toutes' });
    }
    if (filters.direction === 'BULLISH') {
      chips.push({ key: 'direction', label: 'Direction : Haussier' });
    }
    if (filters.direction === 'BEARISH') {
      chips.push({ key: 'direction', label: 'Direction : Baissier' });
    }
    if (filters.direction === 'NEUTRAL') {
      chips.push({ key: 'direction', label: 'Direction : Neutre' });
    }
  }
  if (filters.pattern) {
    chips.push({ key: 'pattern', label: `Pattern : ${getPatternTitle(filters.pattern)}` });
  }
  if (filters.convictionMin !== DEFAULT_FILTERS.convictionMin) {
    chips.push({ key: 'convictionMin', label: `Score >= ${filters.convictionMin}` });
  }
  if (filters.averageScoreMin !== DEFAULT_FILTERS.averageScoreMin) {
    chips.push({ key: 'averageScoreMin', label: `Score moyen >= ${filters.averageScoreMin}` });
  }
  if (filters.confirmedVolumeOnly !== DEFAULT_FILTERS.confirmedVolumeOnly) {
    chips.push({ key: 'confirmedVolumeOnly', label: filters.confirmedVolumeOnly ? 'Volume confirmé' : 'Volume confirmé : non' });
  }
  if (filters.volumeFilter === 'CONFIRMED') {
    chips.push({ key: 'volume', label: 'Volume : confirmé' });
  }
  if (filters.volumeFilter === 'STRONG') {
    chips.push({ key: 'volume', label: 'Volume : très fort' });
  }
  if (filters.advancedOnly) {
    chips.push({ key: 'advancedOnly', label: 'Signaux avancés' });
  }
  if (filters.topMoversOnly) {
    chips.push({ key: 'topMoversOnly', label: 'Top movers' });
  }

  return chips;
}

function readFilters(searchParams) {
  const fallbackFilters = hasExplicitFilterParams(searchParams) ? DEFAULT_FILTERS : readStoredFilters();
  const scoreMinParam = searchParams.get('scoreMin');
  const convictionMinParam = searchParams.get('convictionMin');
  const averageScoreMinParam = searchParams.get('averageScoreMin');
  const assetQueryParam = searchParams.get('asset')
    || searchParams.get('symbol')
    || searchParams.get('search')
    || searchParams.get('query')
    || fallbackFilters.assetQuery;
  const confirmedVolumeOnlyParam = searchParams.get('confirmedVolumeOnly');
  const advancedOnlyParam = searchParams.get('advancedOnly');
  const topMoversOnlyParam = searchParams.get('topMoversOnly');
  return {
    universe: searchParams.get('universe') || fallbackFilters.universe,
    exchange: searchParams.get('exchange') || fallbackFilters.exchange,
    timeframe: searchParams.get('timeframe') || fallbackFilters.timeframe,
    patternEngineVersion: Number(searchParams.get('patternEngineVersion') || fallbackFilters.patternEngineVersion),
    tradeEngineVersion: Number(searchParams.get('tradeEngineVersion') || fallbackFilters.tradeEngineVersion),
    assetQuery: assetQueryParam,
    pattern: searchParams.get('pattern') || fallbackFilters.pattern,
    direction: searchParams.get('direction') || fallbackFilters.direction,
    convictionMin: normalizeScoreThreshold(scoreMinParam || convictionMinParam, fallbackFilters.convictionMin),
    averageScoreMin: normalizeScoreThreshold(averageScoreMinParam, fallbackFilters.averageScoreMin),
    confirmedVolumeOnly: confirmedVolumeOnlyParam === null ? fallbackFilters.confirmedVolumeOnly : confirmedVolumeOnlyParam === 'true',
    volumeFilter: searchParams.get('volumeFilter') || fallbackFilters.volumeFilter,
    advancedOnly: advancedOnlyParam === null ? fallbackFilters.advancedOnly : advancedOnlyParam === 'true',
    topMoversOnly: topMoversOnlyParam === null ? fallbackFilters.topMoversOnly : topMoversOnlyParam === 'true',
    page: Math.max(1, Number(searchParams.get('page') || fallbackFilters.page)),
  };
}

function buildSearchParams(filters) {
  const params = new URLSearchParams();
  params.set('universe', filters.universe);
  params.set('exchange', filters.exchange);
  params.set('timeframe', filters.timeframe);
  if (filters.patternEngineVersion > DEFAULT_PATTERN_ENGINE_VERSION) {
    params.set('patternEngineVersion', String(filters.patternEngineVersion));
  }
  if (filters.tradeEngineVersion > DEFAULT_TRADE_ENGINE_VERSION) {
    params.set('tradeEngineVersion', String(filters.tradeEngineVersion));
  }
  if (filters.assetQuery) {
    params.set('asset', filters.assetQuery);
  }
  if (filters.pattern) {
    params.set('pattern', filters.pattern);
  }
  if (filters.direction !== DEFAULT_FILTERS.direction) {
    params.set('direction', filters.direction);
  }
  if (filters.convictionMin !== DEFAULT_FILTERS.convictionMin) {
    params.set('scoreMin', String(filters.convictionMin));
  }
  if (filters.averageScoreMin !== DEFAULT_FILTERS.averageScoreMin) {
    params.set('averageScoreMin', String(filters.averageScoreMin));
  }
  if (filters.confirmedVolumeOnly !== DEFAULT_FILTERS.confirmedVolumeOnly) {
    params.set('confirmedVolumeOnly', String(filters.confirmedVolumeOnly));
  }
  if (filters.volumeFilter !== 'ALL') {
    params.set('volumeFilter', filters.volumeFilter);
  }
  if (filters.advancedOnly) {
    params.set('advancedOnly', 'true');
  }
  if (filters.topMoversOnly) {
    params.set('topMoversOnly', 'true');
  }
  if (filters.page > 1) {
    params.set('page', String(filters.page));
  }
  return params;
}

function matchesVolumeFilter(row, volumeFilter) {
  if (volumeFilter === 'ALL') {
    return true;
  }
  const score = Number.isFinite(row?.volumeConfirmationScore) ? row.volumeConfirmationScore : 0;
  if (volumeFilter === 'CONFIRMED') {
    return Boolean(row?.volumeConfirmation || score >= 0.6);
  }
  if (volumeFilter === 'STRONG') {
    return Boolean(row?.volumeConfirmation && score >= 0.75);
  }
  return true;
}

function matchesConfirmedVolumeOnly(row, confirmedVolumeOnly) {
  return !confirmedVolumeOnly || matchesVolumeFilter(row, 'CONFIRMED');
}

function resolveRowDirection(row) {
  const displayedPatternKey = resolveDisplayedPatternKey(row);
  if (displayedPatternKey === NO_CLEAR_PATTERN_KEY) {
    return 'NEUTRAL';
  }
  if (BULLISH_DISPLAY_PATTERNS.has(displayedPatternKey)) {
    return 'BULLISH';
  }
  if (BEARISH_DISPLAY_PATTERNS.has(displayedPatternKey)) {
    return 'BEARISH';
  }
  const structureBias = normalizeDirectionCode(resolvePatternEngineV2Metadata(row).structure?.bias);
  if (structureBias !== 'NEUTRAL') {
    return structureBias;
  }
  if (row?.bullish) {
    return 'BULLISH';
  }
  if (row?.bearish) {
    return 'BEARISH';
  }
  return normalizeDirectionCode(row?.direction);
}

function matchesDirectionFilter(row, directionFilter) {
  const direction = resolveRowDirection(row);
  if (directionFilter === 'BULLISH') {
    return direction === 'BULLISH';
  }
  if (directionFilter === 'BEARISH') {
    return direction === 'BEARISH';
  }
  if (directionFilter === 'NEUTRAL') {
    return direction === 'NEUTRAL';
  }
  if (directionFilter === 'NON_NEUTRAL') {
    return direction !== 'NEUTRAL';
  }
  return true;
}

function matchesAssetFilter(row, rawAssetQuery) {
  const assetQuery = String(rawAssetQuery || '').trim().toLowerCase();
  if (!assetQuery) {
    return true;
  }

  const symbol = String(row?.symbol || '').toLowerCase();
  const name = String(row?.name || '').toLowerCase();
  return symbol.includes(assetQuery) || name.includes(assetQuery);
}

function sortRows(rows) {
  return [...rows].sort((left, right) => {
    const scoreDelta = resolveScore(right) - resolveScore(left);
    if (scoreDelta !== 0) {
      return scoreDelta;
    }
    const averageScoreDelta = (resolveAverageScore(right) || 0) - (resolveAverageScore(left) || 0);
    if (averageScoreDelta !== 0) {
      return averageScoreDelta;
    }
    const alignmentDelta = (right?.multiTimeframeDetail?.alignmentScore || 0) - (left?.multiTimeframeDetail?.alignmentScore || 0);
    if (alignmentDelta !== 0) {
      return alignmentDelta;
    }
    return (right?.patternScore || 0) - (left?.patternScore || 0);
  });
}

function SummaryMetric({ label, value, helper, tone }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3.5,
        border: '1px solid rgba(16,37,43,0.08)',
        background: '#fffdf8',
      }}
    >
      <Typography variant="overline" sx={{ color: '#667880', letterSpacing: '0.08em' }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ color: tone, fontWeight: 900, mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: '#5e7077' }}>
        {helper}
      </Typography>
    </Paper>
  );
}

function OpportunityCard({ row, onOpen, patternEngineVersion, tradeEngineVersion }) {
  const displayedPatternKey = resolveDisplayedPatternKey(row) || row?.detectedPattern;
  const geometry = resolvePatternEngineV2Metadata(row).geometry;
  const directionCode = resolveRowDirection(row);
  const direction = resolveDirectionDescriptor(directionCode);
  const score = resolveScore(row);
  const averageScore = resolveAverageScore(row);
  const volume = resolveVolumeDescriptor(row);
  const alignment = resolveAlignmentDescriptor(row);
  const risk = resolveRiskDescriptor(row);
  const directionLabel = `Biais ${direction.label.toLowerCase()}`;
  const directionIcon = directionCode === 'BEARISH'
    ? <SouthEastRoundedIcon />
    : directionCode === 'BULLISH'
      ? <NorthEastRoundedIcon />
      : <InsightsRoundedIcon />;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.2,
        borderRadius: 4,
        border: '1px solid rgba(16,37,43,0.08)',
        background: 'linear-gradient(180deg, rgba(255,248,234,0.98) 0%, rgba(255,255,255,0.98) 100%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.4,
        minHeight: 246,
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1.4} alignItems="flex-start">
        <Box>
          <Typography variant="overline" sx={{ color: '#6b7a80', letterSpacing: '0.12em' }}>
            {row.exchange} · {row.timeframe}
          </Typography>
          <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 900 }}>
            {row.symbol}
          </Typography>
          <Typography variant="body1" sx={{ color: '#43545b', fontWeight: 700 }}>
            {getPatternTitle(displayedPatternKey)}
          </Typography>
        </Box>
        <Chip
          label={directionLabel}
          size="small"
          icon={directionIcon}
          sx={{
            bgcolor: direction.background,
            color: direction.tone,
            fontWeight: 800,
          }}
        />
      </Stack>

      <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
        <Chip label={`Score ${score}`} size="small" sx={{ bgcolor: '#17323c', color: '#fff8eb', fontWeight: 900 }} />
        <Chip label={`Score moyen ${Number.isFinite(averageScore) ? averageScore : 'n/a'}`} size="small" sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800 }} />
        <Chip label={`Volume ${volume.label}`} size="small" sx={{ bgcolor: volume.background, color: volume.tone, fontWeight: 800 }} />
        <Chip label={`MTF ${alignment.label}`} size="small" sx={{ bgcolor: alignment.background, color: alignment.tone, fontWeight: 800 }} />
        <Chip label={`Risque ${risk.label}`} size="small" sx={{ bgcolor: risk.background, color: risk.tone, fontWeight: 800 }} />
        {geometry ? <Chip label={`Géométrie ${resolveGeometryTypeLabel(geometry.type)}`} size="small" sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} /> : null}
      </Stack>

      <Typography variant="body2" sx={{ color: '#56686f', lineHeight: 1.7, flex: 1 }}>
        {row.aiSummary || 'Aucun résumé généré pour cette configuration.'}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
        <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2.5, background: '#f5efe2' }}>
          <Typography variant="caption" sx={{ color: '#6b7a80', display: 'block' }}>Momentum</Typography>
          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>{row.momentumState || 'n/a'}</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2.5, background: '#f5efe2' }}>
          <Typography variant="caption" sx={{ color: '#6b7a80', display: 'block' }}>Fakeout</Typography>
          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>{row.fakeoutRisk}%</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2.5, background: '#f5efe2' }}>
          <Typography variant="caption" sx={{ color: '#6b7a80', display: 'block' }}>Distance cible</Typography>
          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>{formatRelativeTargetMove(row)}</Typography>
        </Paper>
      </Box>

      <Button
        variant="contained"
        endIcon={<ShowChartRoundedIcon />}
        onClick={onOpen}
        sx={{
          alignSelf: 'flex-start',
          borderRadius: 999,
          px: 2.2,
          py: 1,
          textTransform: 'none',
          fontWeight: 800,
          bgcolor: '#17323c',
          '&:hover': { bgcolor: '#214250' },
        }}
      >
        Voir l’analyse
      </Button>
    </Paper>
  );
}

const PatternScannerOverviewPage = ({ user = null, authResolved = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const [convictionMinDraft, setConvictionMinDraft] = useState(() => String(filters.convictionMin));
  const [averageScoreMinDraft, setAverageScoreMinDraft] = useState(() => String(filters.averageScoreMin));
  const [rows, setRows] = useState([]);
  const [analyzedAssetCount, setAnalyzedAssetCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anonymousLimitApplied, setAnonymousLimitApplied] = useState(null);
  const hasAuthenticatedUser = Boolean(user?.login);
  const isGuestScannerExperience = authResolved ? (anonymousLimitApplied ?? !hasAuthenticatedUser) : false;
  const areFiltersEditable = authResolved && !isGuestScannerExperience;
  const effectivePatternEngineVersion = isGuestScannerExperience ? DEFAULT_PATTERN_ENGINE_VERSION : filters.patternEngineVersion;
  const effectiveTradeEngineVersion = isGuestScannerExperience ? DEFAULT_TRADE_ENGINE_VERSION : filters.tradeEngineVersion;
  const effectiveFilters = useMemo(() => ({
    ...filters,
    patternEngineVersion: effectivePatternEngineVersion,
    tradeEngineVersion: effectiveTradeEngineVersion,
  }), [effectivePatternEngineVersion, effectiveTradeEngineVersion, filters]);

  useEffect(() => {
    setConvictionMinDraft(String(filters.convictionMin));
  }, [filters.convictionMin]);

  useEffect(() => {
    setAverageScoreMinDraft(String(filters.averageScoreMin));
  }, [filters.averageScoreMin]);

  useEffect(() => {
    let cancelled = false;

    const loadRows = async () => {
      setLoading(true);
      setError('');
      try {
        let payload = await fetchScannerOverview(effectiveFilters);
        if (shouldRetryScannerBootstrap(effectiveFilters, payload.rows)) {
          payload = await fetchScannerOverview(effectiveFilters, { forceRefresh: true });
        }
        if (!cancelled) {
          const shouldApplyAnonymousLimit = payload.anonymousLimitApplied ?? (authResolved ? !hasAuthenticatedUser : false);
          setAnonymousLimitApplied(shouldApplyAnonymousLimit);
          setAnalyzedAssetCount(Number.isFinite(payload.analyzedAssetCount) ? payload.analyzedAssetCount : null);
          setRows(shouldApplyAnonymousLimit
            ? payload.rows.slice(0, ANONYMOUS_SCANNER_RESULT_LIMIT)
            : payload.rows);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError('Le scanner d’opportunités est indisponible pour le moment.');
          setAnalyzedAssetCount(null);
          setAnonymousLimitApplied(null);
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadRows();
    return () => {
      cancelled = true;
    };
  }, [authResolved, effectiveFilters, hasAuthenticatedUser]);

  useEffect(() => {
    storeFilters(effectiveFilters);
  }, [effectiveFilters]);

  useEffect(() => {
    if (!authResolved || !user?.login) {
      return undefined;
    }

    const abortController = new AbortController();
    void recordPatternScannerPageView({
      timeframe: filters.timeframe,
      signal: abortController.signal,
    });

    return () => {
      abortController.abort();
    };
  }, [authResolved, filters.timeframe, user?.login]);

  const setFilter = (patch) => {
    if (!areFiltersEditable) {
      return;
    }
    const nextFilters = {
      ...filters,
      ...patch,
      page: patch.page ?? (Object.prototype.hasOwnProperty.call(patch, 'page') ? patch.page : 1),
    };
    setSearchParams(buildSearchParams(nextFilters), { replace: true });
  };

  const resetFilters = () => {
    if (!areFiltersEditable) {
      return;
    }
    setSearchParams(buildSearchParams(DEFAULT_FILTERS), { replace: true });
  };

  const commitScoreThreshold = (key, rawValue, setDraft) => {
    const fallback = DEFAULT_FILTERS[key];
    const normalizedValue = normalizeScoreThreshold(rawValue, fallback);
    setDraft(String(normalizedValue));
    if (filters[key] !== normalizedValue) {
      setFilter({ [key]: normalizedValue });
    }
  };

  const openDetail = (row) => {
    const detailSearchParams = new URLSearchParams({
      exchange: effectiveFilters.exchange,
      timeframe: effectiveFilters.timeframe,
    });
    if (effectivePatternEngineVersion > DEFAULT_PATTERN_ENGINE_VERSION) {
      detailSearchParams.set('patternEngineVersion', String(effectivePatternEngineVersion));
    }
    if (effectiveTradeEngineVersion > DEFAULT_TRADE_ENGINE_VERSION) {
      detailSearchParams.set('tradeEngineVersion', String(effectiveTradeEngineVersion));
    }

    navigate(`/scanner/${encodeURIComponent(row.symbol)}?${detailSearchParams.toString()}`, {
      state: {
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  };

  const filteredRows = useMemo(() => sortRows(rows.filter((row) => {
    if (!matchesAssetFilter(row, filters.assetQuery)) {
      return false;
    }
    if (filters.pattern && resolveDisplayedPatternKey(row) !== filters.pattern) {
      return false;
    }
    if (!matchesDirectionFilter(row, filters.direction)) {
      return false;
    }
    if (resolveScore(row) < filters.convictionMin) {
      return false;
    }
    if ((resolveAverageScore(row) || 0) < filters.averageScoreMin) {
      return false;
    }
    if (!matchesConfirmedVolumeOnly(row, filters.confirmedVolumeOnly)) {
      return false;
    }
    if (!matchesVolumeFilter(row, filters.volumeFilter)) {
      return false;
    }
    if (filters.advancedOnly && !hasAdvancedSignalCoverage(row)) {
      return false;
    }
    return true;
  })), [filters.advancedOnly, filters.assetQuery, filters.averageScoreMin, filters.confirmedVolumeOnly, filters.convictionMin, filters.direction, filters.pattern, filters.volumeFilter, rows]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / SCANNER_PAGE_SIZE));
  const currentPage = Math.min(filters.page, pageCount);
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * SCANNER_PAGE_SIZE;
    return filteredRows.slice(startIndex, startIndex + SCANNER_PAGE_SIZE);
  }, [currentPage, filteredRows]);

  const summary = useMemo(() => ({
    analyzed: analyzedAssetCount ?? rows.length,
    detected: filteredRows.length,
    strong: filteredRows.filter((row) => resolveScore(row) >= 80).length,
    institutional: filteredRows.filter((row) => (resolveAverageScore(row) || 0) >= 70).length,
  }), [analyzedAssetCount, filteredRows, rows.length]);

  const activeFilterChips = useMemo(() => buildActiveFilterChips(effectiveFilters), [effectiveFilters]);
  const spotlightRows = useMemo(() => filteredRows.slice(0, 3), [filteredRows]);

  return (
    <Box sx={{ minHeight: '100vh', background: 'radial-gradient(circle at top, rgba(243,198,92,0.18), transparent 32%), linear-gradient(180deg, #fbf7ee 0%, #f4efe4 100%)', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Paper elevation={0} sx={{ p: { xs: 2.4, md: 3.2 }, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #264653 100%)', color: '#fff8eb' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2.2, alignItems: 'stretch' }}>
              <Stack spacing={1.1}>
                <Typography variant="overline" sx={{ letterSpacing: '0.12em', opacity: 0.78 }}>Détection d’opportunités</Typography>
                <Typography variant="h3" sx={{ fontWeight: 900 }}>Pattern Scanner</Typography>
                <Typography variant="h6" sx={{ maxWidth: 780, color: 'rgba(255,248,235,0.86)', lineHeight: 1.65 }}>
                  Détection automatique des meilleures configurations chartistes.
                </Typography>
                {isGuestScannerExperience ? (
                  <Alert
                    severity="info"
                    icon={false}
                    sx={{
                      mt: 0.6,
                      maxWidth: 780,
                      borderRadius: 3,
                      bgcolor: 'rgba(255,248,235,0.12)',
                      color: '#fff8eb',
                      border: '1px solid rgba(255,248,235,0.16)',
                      '& .MuiAlert-message': { width: '100%' },
                    }}
                  >
                    Mode invité : le scanner analyse l’univers complet mais affiche jusqu’à 3 résultats et les filtres sont verrouillés. Connectez-vous pour débloquer toute la short-list et modifier le filtrage.
                  </Alert>
                ) : null}
              </Stack>

              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, bgcolor: 'rgba(255,248,235,0.1)', border: '1px solid rgba(255,248,235,0.14)' }}>
                <Stack spacing={1.2}>
                  <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: 'rgba(255,248,235,0.74)' }}>
                    Opportunités immédiates
                  </Typography>
                  {spotlightRows.length > 0 ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                      {spotlightRows.map((row, index) => (
                        <Button
                          key={`${row.symbol}-${row.exchange}-${row.timeframe}-spotlight`}
                          variant="text"
                          onClick={() => openDetail(row)}
                          sx={{
                            px: 1.35,
                            py: 1.2,
                            minHeight: 118,
                            borderRadius: 3,
                            color: '#fff8eb',
                            bgcolor: 'rgba(255,248,235,0.08)',
                            textTransform: 'none',
                            alignItems: 'stretch',
                            '&:hover': { bgcolor: 'rgba(255,248,235,0.14)' },
                          }}
                        >
                          <Stack spacing={1.1} sx={{ width: '100%' }}>
                            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                              <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ color: '#fff8eb', fontWeight: 900 }}>{row.symbol}</Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,248,235,0.72)', display: 'block', lineHeight: 1.5 }}>
                                  {getPatternTitle(resolveDisplayedPatternKey(row) || row.detectedPattern)}
                                </Typography>
                                {resolvePatternEngineV2Metadata(row).geometry ? (
                                  <Typography variant="caption" sx={{ color: 'rgba(255,248,235,0.62)', display: 'block', lineHeight: 1.5 }}>
                                    {`Géométrie ${resolveGeometryTypeLabel(resolvePatternEngineV2Metadata(row).geometry.type)}`}
                                  </Typography>
                                ) : null}
                              </Box>
                              <Chip label={`#${index + 1}`} size="small" sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 900, flexShrink: 0 }} />
                            </Stack>
                            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                              <Chip label={`Score ${resolveScore(row)}`} size="small" sx={{ bgcolor: 'rgba(255,248,235,0.16)', color: '#fff8eb', fontWeight: 800 }} />
                              <Chip label={`Moyen ${Number.isFinite(resolveAverageScore(row)) ? resolveAverageScore(row) : 'n/a'}`} size="small" sx={{ bgcolor: 'rgba(243,198,92,0.18)', color: '#fff8eb', fontWeight: 800 }} />
                              <Chip label={`Pattern v${effectivePatternEngineVersion}`} size="small" sx={{ bgcolor: 'rgba(255,248,235,0.16)', color: '#fff8eb', fontWeight: 800 }} />
                            </Stack>
                          </Stack>
                        </Button>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255,248,235,0.74)', lineHeight: 1.7 }}>
                      Le radar se remplira ici dès qu’une short-list exploitable sera disponible.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: { xs: 2, md: 2.4 }, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)' }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 800 }}>Filtres</Typography>
                <Button
                  variant="text"
                  onClick={resetFilters}
                  disabled={!areFiltersEditable || activeFilterChips.length === 0}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  Réinitialiser les filtres
                </Button>
              </Stack>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>
                  Modes moteurs
                </Typography>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.2} flexWrap="wrap" useFlexGap>
                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap alignItems="center">
                    <Typography variant="body2" sx={{ color: '#5c6d74', fontWeight: 700 }}>
                      Lecture pattern
                    </Typography>
                    {AVAILABLE_PATTERN_ENGINE_VERSIONS.map((version) => (
                      <Button
                        key={`pattern-engine-${version}`}
                        size="small"
                        variant={effectivePatternEngineVersion === version ? 'contained' : 'outlined'}
                        disabled={!areFiltersEditable}
                        onClick={() => setFilter({ patternEngineVersion: version })}
                        sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
                      >
                        {`Pattern v${version}`}
                      </Button>
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap alignItems="center">
                    <Typography variant="body2" sx={{ color: '#5c6d74', fontWeight: 700 }}>
                      Exécution trade
                    </Typography>
                    {AVAILABLE_TRADE_ENGINE_VERSIONS.map((version) => (
                      <Button
                        key={`trade-engine-${version}`}
                        size="small"
                        variant={effectiveTradeEngineVersion === version ? 'contained' : 'outlined'}
                        disabled={!areFiltersEditable}
                        onClick={() => setFilter({ tradeEngineVersion: version })}
                        sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
                      >
                        {`Trade v${version}`}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' }, gap: 1.2 }}>
                <TextField
                  label="Actif"
                  size="small"
                  value={filters.assetQuery}
                  disabled={!areFiltersEditable}
                  onChange={(event) => setFilter({ assetQuery: event.target.value })}
                  placeholder="BTC, ETH, SOL..."
                />
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Universe</InputLabel>
                  <Select label="Universe" value={filters.universe} onChange={(event) => setFilter({ universe: event.target.value })}>
                    <MenuItem value="top50">Top 50</MenuItem>
                    <MenuItem value="top100">Top 100</MenuItem>
                    <MenuItem value="top300">Top 300</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Timeframe</InputLabel>
                  <Select label="Timeframe" value={filters.timeframe} onChange={(event) => setFilter({ timeframe: event.target.value })}>
                    <MenuItem value="1H">1H</MenuItem>
                    <MenuItem value="4H">4H</MenuItem>
                    <MenuItem value="1D">1D</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Version pattern</InputLabel>
                  <Select
                    label="Version pattern"
                    value={effectivePatternEngineVersion}
                    onChange={(event) => setFilter({ patternEngineVersion: Number(event.target.value) })}
                  >
                    {AVAILABLE_PATTERN_ENGINE_VERSIONS.map((version) => (
                      <MenuItem key={version} value={version}>{`Pattern v${version}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Version trade</InputLabel>
                  <Select
                    label="Version trade"
                    value={effectiveTradeEngineVersion}
                    onChange={(event) => setFilter({ tradeEngineVersion: Number(event.target.value) })}
                  >
                    {AVAILABLE_TRADE_ENGINE_VERSIONS.map((version) => (
                      <MenuItem key={version} value={version}>{`Trade v${version}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Direction</InputLabel>
                  <Select label="Direction" value={filters.direction} onChange={(event) => setFilter({ direction: event.target.value })}>
                    <MenuItem value="NON_NEUTRAL">Non neutre</MenuItem>
                    <MenuItem value="ALL">Toutes</MenuItem>
                    <MenuItem value="BULLISH">Haussières</MenuItem>
                    <MenuItem value="BEARISH">Baissières</MenuItem>
                    <MenuItem value="NEUTRAL">Neutre</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" disabled={!areFiltersEditable}>
                  <InputLabel>Volume</InputLabel>
                  <Select label="Volume" value={filters.volumeFilter} onChange={(event) => setFilter({ volumeFilter: event.target.value })}>
                    <MenuItem value="ALL">Tous</MenuItem>
                    <MenuItem value="CONFIRMED">Confirmé</MenuItem>
                    <MenuItem value="STRONG">Très fort</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Score min"
                  size="small"
                  type="number"
                  value={convictionMinDraft}
                  disabled={!areFiltersEditable}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    if (/^\d*$/.test(nextValue)) {
                      setConvictionMinDraft(nextValue);
                    }
                  }}
                  onBlur={() => commitScoreThreshold('convictionMin', convictionMinDraft, setConvictionMinDraft)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      commitScoreThreshold('convictionMin', convictionMinDraft, setConvictionMinDraft);
                    }
                  }}
                  inputProps={{ min: MIN_SCORE_THRESHOLD, max: MAX_SCORE_THRESHOLD, step: 1, inputMode: 'numeric' }}
                />
                <TextField
                  label="Score moyen min"
                  size="small"
                  type="number"
                  value={averageScoreMinDraft}
                  disabled={!areFiltersEditable}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    if (/^\d*$/.test(nextValue)) {
                      setAverageScoreMinDraft(nextValue);
                    }
                  }}
                  onBlur={() => commitScoreThreshold('averageScoreMin', averageScoreMinDraft, setAverageScoreMinDraft)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      commitScoreThreshold('averageScoreMin', averageScoreMinDraft, setAverageScoreMinDraft);
                    }
                  }}
                  inputProps={{ min: MIN_SCORE_THRESHOLD, max: MAX_SCORE_THRESHOLD, step: 1, inputMode: 'numeric' }}
                />
              </Box>

              <Accordion disableGutters elevation={0} sx={{ borderRadius: 3.2, border: '1px solid rgba(16,37,43,0.08)', overflow: 'hidden', background: '#fcfbf7' }}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ background: '#fff' }}>
                  <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900 }}>
                    Plus de filtres
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1.2}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 1.2 }}>
                      <FormControl size="small" disabled={!areFiltersEditable}>
                        <InputLabel>Exchange</InputLabel>
                        <Select label="Exchange" value={filters.exchange} onChange={(event) => setFilter({ exchange: event.target.value })}>
                          <MenuItem value="BINANCE">Binance</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" disabled={!areFiltersEditable}>
                        <InputLabel>Pattern</InputLabel>
                        <Select label="Pattern" value={filters.pattern} onChange={(event) => setFilter({ pattern: event.target.value })}>
                          <MenuItem value="">Tous</MenuItem>
                          {managedPatternOptions.map((pattern) => (
                            <MenuItem key={pattern.value} value={pattern.value}>{pattern.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2}>
                      <FormControlLabel control={<Switch checked={filters.confirmedVolumeOnly} disabled={!areFiltersEditable} onChange={(event) => setFilter({ confirmedVolumeOnly: event.target.checked })} />} label="Volume confirmé" />
                      <FormControlLabel control={<Switch checked={filters.advancedOnly} disabled={!areFiltersEditable} onChange={(event) => setFilter({ advancedOnly: event.target.checked })} />} label="Signaux avancés uniquement" />
                      <FormControlLabel control={<Switch checked={filters.topMoversOnly} disabled={!areFiltersEditable} onChange={(event) => setFilter({ topMoversOnly: event.target.checked })} />} label="Top movers uniquement" />
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Box>
                <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800, mb: 0.8 }}>
                  Filtres actifs
                </Typography>
                {activeFilterChips.length > 0 ? (
                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                    {activeFilterChips.map((chip) => (
                      <Chip key={chip.key} label={chip.label} size="small" sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: '#5c6d74' }}>
                    Aucun filtre additionnel actif. La short-list reflète le cadrage standard du scanner.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Paper>

          {loading ? <LinearProgress sx={{ height: 8, borderRadius: 999 }} /> : null}
          {error ? <Alert severity="warning">{error}</Alert> : null}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.2 }}>
            <SummaryMetric label="Actifs analysés" value={summary.analyzed} helper="Couverture exploitable sur la fenêtre courante." tone="#17323c" />
            <SummaryMetric label="Setups détectés" value={summary.detected} helper="Short-list après tri score décroissant." tone="#214f77" />
            <SummaryMetric label="Scores forts" value={summary.strong} helper="Score au moins égal à 80." tone="#1f7a4d" />
            <SummaryMetric label="MTF solides" value={summary.institutional} helper="Score moyen 1H/4H/1D au moins égal à 70." tone="#8d6c16" />
          </Box>

          <Paper elevation={0} sx={{ p: { xs: 2, md: 2.4 }, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 900 }}>Top opportunités</Typography>
                <Typography variant="body2" sx={{ color: '#5c6d74' }}>
                  Les cartes sont triées par score décroissant pour mettre d’abord les setups les plus actionnables.
                </Typography>
              </Box>
              <Chip icon={<InsightsRoundedIcon />} label={`Page ${currentPage}/${pageCount} · Top ${SCANNER_PAGE_SIZE}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
            </Stack>

            {(!loading && paginatedRows.length === 0) ? (
              <Alert severity="info">Aucune opportunité ne correspond aux filtres actifs.</Alert>
            ) : null}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
              {paginatedRows.map((row) => (
                <OpportunityCard
                  key={`${row.symbol}-${row.exchange}-${row.timeframe}`}
                  row={row}
                  onOpen={() => openDetail(row)}
                  patternEngineVersion={effectivePatternEngineVersion}
                  tradeEngineVersion={effectiveTradeEngineVersion}
                />
              ))}
            </Box>

            {pageCount > 1 ? (
              <Stack direction="row" justifyContent="center" sx={{ mt: 2.4 }}>
                <Pagination page={currentPage} count={pageCount} color="primary" onChange={(_, page) => setFilter({ page })} />
              </Stack>
            ) : null}
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
};

export default PatternScannerOverviewPage;
